import {
  CURRENT_STATION_EMOJI,
  IPassengerInfo,
  IPassengerTooltipInfo,
  PASSENGER_STATION_MATCHES_OPPOSITE_RIDE_STATION_EMOJI,
  PASSENGER_TOOLTIP_FETCH_TIMEOUT_MS,
  RidesApi,
} from "@ryde/shared";

const passengerTooltipInfoCache = new Map<number, IPassengerTooltipInfo>();
const passengerTooltipInfoPending = new Map<
  number,
  Promise<IPassengerTooltipInfo | undefined>
>();
const passengerTooltipAbortControllers = new Map<number, AbortController>();

export const getCachedPassengerTooltipInfo = (
  passengerId: number,
): IPassengerTooltipInfo | undefined =>
  passengerTooltipInfoCache.get(passengerId);

export const cancelPassengerTooltipInfoLoad = (passengerId: number): void => {
  passengerTooltipAbortControllers.get(passengerId)?.abort();
  passengerTooltipAbortControllers.delete(passengerId);
  passengerTooltipInfoPending.delete(passengerId);
};

const loadPassengerTooltipInfo = async (
  passengerId: number,
): Promise<IPassengerTooltipInfo | undefined> => {
  cancelPassengerTooltipInfoLoad(passengerId);

  const abortController = new AbortController();
  passengerTooltipAbortControllers.set(passengerId, abortController);

  const timeoutId = window.setTimeout(() => {
    abortController.abort();
  }, PASSENGER_TOOLTIP_FETCH_TIMEOUT_MS);

  try {
    const tooltipInfosResult = await RidesApi.fetchPassengerTooltipInfos(
      [passengerId],
      abortController,
    );

    if (!tooltipInfosResult?.success) {
      return undefined;
    }

    const loadedInfo = tooltipInfosResult.data?.find(
      item => item.id === passengerId,
    );

    if (loadedInfo) {
      passengerTooltipInfoCache.set(passengerId, loadedInfo);
    }

    return loadedInfo;
  } catch {
    return undefined;
  } finally {
    window.clearTimeout(timeoutId);
    passengerTooltipAbortControllers.delete(passengerId);
    passengerTooltipInfoPending.delete(passengerId);
  }
};

export const fetchPassengerTooltipInfo = async (
  passengerId: number,
): Promise<IPassengerTooltipInfo | undefined> => {
  const cachedInfo = passengerTooltipInfoCache.get(passengerId);
  if (cachedInfo) {
    return cachedInfo;
  }

  const pendingRequest = passengerTooltipInfoPending.get(passengerId);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = loadPassengerTooltipInfo(passengerId);
  passengerTooltipInfoPending.set(passengerId, request);
  return request;
};

export const mergePassengerTooltipInfo = ({
  loadedInfo,
  passenger,
}: {
  loadedInfo?: IPassengerTooltipInfo;
  passenger: IPassengerInfo;
}): IPassengerTooltipInfo => {
  // After API load, trust server projection (already gated by site/unit read).
  if (loadedInfo) {
    return loadedInfo;
  }

  // Pre-fetch snapshot from ride passenger (kanban usually has unitId/unitName).
  return {
    id: passenger.id,
    firstName: passenger.firstName,
    lastName: passenger.lastName,
    code: passenger.code,
    siteId: passenger.siteId,
    unitId: passenger.unitId,
    unitName: passenger.unitName,
    passengerTypeId: passenger.passengerTypeId,
    stations: [],
  };
};

export const requestPassengerTooltipInfoLoad = ({
  passengerId,
  onLoaded,
}: {
  passengerId: number;
  onLoaded: (info: IPassengerTooltipInfo) => void;
}): void => {
  const cachedInfo = getCachedPassengerTooltipInfo(passengerId);
  if (cachedInfo) {
    onLoaded(cachedInfo);
    return;
  }

  void fetchPassengerTooltipInfo(passengerId).then(loadedInfo => {
    if (loadedInfo) {
      onLoaded(loadedInfo);
    }
  });
};

export const getPassengerCardStationEmoji = ({
  stationId,
  currentStationId,
  oppositeRideStationId,
}: {
  stationId: number;
  currentStationId?: number;
  oppositeRideStationId?: number;
}): string | undefined => {
  if (currentStationId !== undefined && stationId === currentStationId) {
    return CURRENT_STATION_EMOJI;
  }

  if (
    oppositeRideStationId !== undefined &&
    stationId === oppositeRideStationId
  ) {
    return PASSENGER_STATION_MATCHES_OPPOSITE_RIDE_STATION_EMOJI;
  }

  return undefined;
};
